import type { Dot, NeckDiagram } from "./model.js";

const LETTERS = "CDEFGAB";
const NATURAL = [0, 2, 4, 5, 7, 9, 11];
export const mod12 = (n: number): number => ((n % 12) + 12) % 12;
export function pitchClass(pitch: string): number {
  const match = /^([A-G])([#b]{0,2})/.exec(pitch);
  if (!match) throw new Error("Use a pitch such as C, F# or Bb.");
  return mod12(
    NATURAL[LETTERS.indexOf(match[1]!)]! +
      [...match[2]!].reduce((sum, c) => sum + (c === "#" ? 1 : -1), 0),
  );
}
export function midi(pitch: string): number {
  const match = /^([A-G])([#b]{0,2})([0-8])$/.exec(pitch);
  if (!match) throw new Error("Use a pitch and octave, such as E2 or Bb3.");
  return (
    12 * (Number(match[3]) + 1) +
    NATURAL[LETTERS.indexOf(match[1]!)]! +
    [...match[2]!].reduce((sum, c) => sum + (c === "#" ? 1 : -1), 0)
  );
}
export interface Formula {
  semitones: readonly number[];
  degrees: readonly string[];
}
const formula = (semitones: number[], degrees: string[]): Formula => ({
  semitones,
  degrees,
});
export const SCALES: Record<string, Formula> = {
  major: formula([0, 2, 4, 5, 7, 9, 11], ["R", "2", "3", "4", "5", "6", "7"]),
  "natural minor": formula(
    [0, 2, 3, 5, 7, 8, 10],
    ["R", "2", "b3", "4", "5", "b6", "b7"],
  ),
  "harmonic minor": formula(
    [0, 2, 3, 5, 7, 8, 11],
    ["R", "2", "b3", "4", "5", "b6", "7"],
  ),
  "melodic minor": formula(
    [0, 2, 3, 5, 7, 9, 11],
    ["R", "2", "b3", "4", "5", "6", "7"],
  ),
  "major pentatonic": formula([0, 2, 4, 7, 9], ["R", "2", "3", "5", "6"]),
  "minor pentatonic": formula([0, 3, 5, 7, 10], ["R", "b3", "4", "5", "b7"]),
  blues: formula([0, 3, 5, 6, 7, 10], ["R", "b3", "4", "b5", "5", "b7"]),
  "major blues": formula([0, 2, 3, 4, 7, 9], ["R", "2", "b3", "3", "5", "6"]),
  Dorian: formula(
    [0, 2, 3, 5, 7, 9, 10],
    ["R", "2", "b3", "4", "5", "6", "b7"],
  ),
  Phrygian: formula(
    [0, 1, 3, 5, 7, 8, 10],
    ["R", "b2", "b3", "4", "5", "b6", "b7"],
  ),
  Lydian: formula([0, 2, 4, 6, 7, 9, 11], ["R", "2", "3", "#4", "5", "6", "7"]),
  Mixolydian: formula(
    [0, 2, 4, 5, 7, 9, 10],
    ["R", "2", "3", "4", "5", "6", "b7"],
  ),
  Locrian: formula(
    [0, 1, 3, 5, 6, 8, 10],
    ["R", "b2", "b3", "4", "b5", "b6", "b7"],
  ),
  chromatic: formula(
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
    ["R", "b2", "2", "b3", "3", "4", "#4", "5", "b6", "6", "b7", "7"],
  ),
};
export const CHORDS: Record<string, Formula> = {
  major: formula([0, 4, 7], ["R", "3", "5"]),
  minor: formula([0, 3, 7], ["R", "b3", "5"]),
  "7": formula([0, 4, 7, 10], ["R", "3", "5", "b7"]),
  maj7: formula([0, 4, 7, 11], ["R", "3", "5", "7"]),
  m7: formula([0, 3, 7, 10], ["R", "b3", "5", "b7"]),
  sus2: formula([0, 2, 7], ["R", "2", "5"]),
  sus4: formula([0, 5, 7], ["R", "4", "5"]),
  add9: formula([0, 4, 7, 2], ["R", "3", "5", "9"]),
  "6": formula([0, 4, 7, 9], ["R", "3", "5", "6"]),
  m6: formula([0, 3, 7, 9], ["R", "b3", "5", "6"]),
  "9": formula([0, 4, 7, 10, 2], ["R", "3", "5", "b7", "9"]),
  dim: formula([0, 3, 6], ["R", "b3", "b5"]),
  dim7: formula([0, 3, 6, 9], ["R", "b3", "b5", "bb7"]),
  aug: formula([0, 4, 8], ["R", "3", "#5"]),
  m7b5: formula([0, 3, 6, 10], ["R", "b3", "b5", "b7"]),
  "5": formula([0, 7], ["R", "5"]),
};
export const ARPEGGIOS = Object.fromEntries(
  ["major", "minor", "7", "maj7", "m7", "m7b5", "dim", "dim7", "aug"].map(
    (name) => [name, CHORDS[name]!],
  ),
) as Record<string, Formula>;
export function sourceFormula(diagram: NeckDiagram): Formula | undefined {
  return diagram.source
    ? (diagram.source.type === "scale" ? SCALES : CHORDS)[diagram.source.name]
    : undefined;
}
export function intervalFor(semitones: number, source?: Formula): string {
  const offset = mod12(semitones);
  const i = source?.semitones.indexOf(offset) ?? -1;
  return i >= 0 ? source!.degrees[i]! : SCALES.chromatic!.degrees[offset]!;
}
/** Choose the degree's letter first; enharmonic spelling then follows the key. */
export function spellNote(
  root: string,
  semitones: number,
  degree: string,
): string {
  const degreeNumber =
    degree === "R" ? 1 : Number(degree.replace(/[^0-9]/g, ""));
  const letter = (LETTERS.indexOf(root[0]!) + degreeNumber - 1) % 7;
  let alteration = mod12(pitchClass(root) + semitones - NATURAL[letter]!);
  if (alteration > 6) alteration -= 12;
  return (
    LETTERS[letter]! +
    (alteration > 0 ? "#".repeat(alteration) : "b".repeat(-alteration))
  );
}
export function dotLabel(diagram: NeckDiagram, dot: Dot): string {
  if (diagram.labelMode === "none") return "";
  if (diagram.labelMode === "finger") return String(dot.finger ?? "");
  if (diagram.labelMode === "custom") return dot.text ?? "";
  const root = diagram.root ?? "C";
  const offset = mod12(
    midi(diagram.tuning[dot.string]!) + dot.fret - pitchClass(root),
  );
  const degree = intervalFor(offset, sourceFormula(diagram));
  return diagram.labelMode === "interval"
    ? degree
    : spellNote(root, offset, degree);
}
export function fillNotes(
  diagram: NeckDiagram,
  root: string,
  name: string,
  type: "scale" | "arpeggio",
): NeckDiagram {
  const selected = (type === "scale" ? SCALES : ARPEGGIOS)[name];
  if (!selected) throw new Error("Choose a scale or arpeggio from the list.");
  const dots: Dot[] = [];
  diagram.tuning.forEach((pitch, string) => {
    for (let fret = diagram.fretFrom; fret <= diagram.fretTo; fret++) {
      const offset = mod12(midi(pitch) + fret - pitchClass(root));
      if (selected.semitones.includes(offset))
        dots.push({
          string,
          fret,
          colour: offset === 0 ? "red" : "black",
          shape: offset === 0 ? "diamond" : "circle",
        });
    }
  });
  return {
    ...diagram,
    root,
    dots,
    barres: [],
    stringMarks: diagram.tuning.map(() => null),
    labelMode: "note",
    source: { type, name, root },
    title: `${root} ${name}${type === "arpeggio" ? " arpeggio" : ""}`,
  };
}
